/*
  The gift note screen (update 3, chapters 1 to 3; stage א7): AMOR writes the
  note for the gift with the shopper, in a conversation, over the whole cart,
  the same pattern as "your look" (snippets/greeting.liquid).
  - "Write me a note" in the gift block opens it, even while gift wrap is off.
  - "Back to the bag" or Esc goes back, and the field does not change.
  - "Choose this note" goes back with gift wrap on and the note in the field
    (AmorCart.useNote in cart.js).

  The server remembers nothing: every request sends the whole conversation,
  each answer exactly as it came back with role "amor", the items in the bag
  (title, vendor, type; never a price) and a signed-in visitor's first name,
  through window.AmorAI.greeting ("הודעה למסלול א - ברכה"). The conversation
  is kept in DemoStore with the signed-in state it was written in, so a
  guest's conversation never goes out with Noa's name; signing in or out,
  "new conversation" and ?reset=1 clear it. Nothing is sent on load: only
  sending, a pill or "try again".

  Errors: "something went wrong" with "try again"; a visitor over her own
  limit (rate_limited) gets the stage א7 sentence with Women and Men and no
  "try again"; the site's daily limit (site_limit) gets "something went
  wrong" with Women and Men and no "try again", as on the answer card.
*/
(() => {
  const ITEMS_MAX = 10;
  const TEXT_MAX = 300;
  const LIMITS = ['rate_limited', 'site_limit'];
  const FALLBACK_LINK = '/pages/amor-by-you';

  const store = () => window.DemoStore;
  const ai = () => window.AmorAI;
  const normalize = (text) => String(text || '').replace(/\s+/g, ' ').trim();
  const hoverDevice = window.matchMedia('(hover: hover)');

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class AmorGreeting extends HTMLElement {
    connectedCallback() {
      this.cart = this.closest('amor-cart');
      this.drawer = this.closest('amor-drawer');
      this.scroller = this.querySelector('[data-greeting-scroll]');
      this.log = this.querySelector('[data-greeting-log]');
      this.itemList = this.querySelector('[data-greeting-items]');
      this.pills = this.querySelector('[data-greeting-pills]');
      this.composer = this.querySelector('[data-greeting-composer]');
      this.form = this.querySelector('[data-greeting-form]');
      this.input = this.querySelector('[data-greeting-input]');
      this.sendButton = this.querySelector('[data-greeting-send]');
      this.empty = this.querySelector('[data-greeting-empty]');
      this.newButton = this.querySelector('[data-greeting-new]');
      this.status = this.querySelector('[data-greeting-status]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-greeting-strings]')?.textContent || '{}');
      } catch {
        this.strings = {};
      }
      this.token = 0;
      this.pending = false;
      this.failure = null;

      this.cart?.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-greeting-open]');
        if (opener) this.open(opener);
      });
      this.addEventListener('click', (event) => this.onClick(event));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.sendText(this.input.value);
      });
      this.input.addEventListener('input', () => this.showEmpty(false));
      // Esc goes back to the bag, not out of the drawer (update 3, chapter 3).
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.isActive()) return;
          event.preventDefault();
          event.stopPropagation();
          this.close(true);
        },
        true
      );
      // The drawer always opens on the bag.
      this.drawer?.addEventListener('drawer:close', () => this.close(false));
      this.drawer?.addEventListener('drawer:open', () => this.close(false));
      // Signing in or out starts the conversation again, addressed the right way.
      document.addEventListener('demo-store:change', (event) => {
        const key = event.detail?.key;
        if (key === 'signedIn') this.forget();
        if ((key === 'signedIn' || key === 'details') && this.isOpen) this.render();
      });
    }

    get isOpen() {
      return !this.hidden;
    }

    /** Open, and the one taking keys: inside an open drawer, or on the cart page with no drawer over it. */
    isActive() {
      if (!this.isOpen) return false;
      const dialog = this.closest('dialog');
      return dialog ? dialog.open : !document.querySelector('dialog[open]');
    }

    onClick(event) {
      if (event.target.closest('[data-greeting-back]')) {
        this.close(true);
        return;
      }
      if (event.target.closest('[data-greeting-new]')) {
        this.startOver();
        return;
      }
      if (event.target.closest('[data-greeting-retry]')) {
        this.retry();
        return;
      }
      const pill = event.target.closest('[data-greeting-pill]');
      if (pill) {
        this.sendText(pill.textContent);
        return;
      }
      const choose = event.target.closest('[data-greeting-choose]');
      if (choose) this.choose(Number(choose.dataset.greetingChoose));
    }

    /* --- Opening and closing ---------------------------------------------------- */

    open(trigger) {
      this.trigger = trigger || null;
      this.hidden = false;
      this.cart?.classList.add('is-note-open');
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.render();
      if (!this.drawer) window.scrollTo({ top: 0 });
      this.querySelector('[data-greeting-title]')?.focus({ preventScroll: true });
    }

    /** Back to the bag; the field and the switch stay as they were. */
    close(restoreFocus) {
      if (!this.isOpen) return;
      this.hidden = true;
      this.cart?.classList.remove('is-note-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.showEmpty(false);
      if (restoreFocus) this.trigger?.focus();
    }

    /** "Choose this note": back to the bag, with gift wrap on and the note in the field. */
    choose(index) {
      const message = this.chat().messages[index];
      if (!message?.greeting) return;
      this.close(false);
      this.cart?.useNote(message.greeting);
      this.cart?.querySelector('[data-gift-panel]')?.scrollIntoView({ block: 'nearest' });
      this.trigger?.focus({ preventScroll: true });
    }

    /* --- The conversation in the demo layer ------------------------------------ */

    /** The kept conversation, only when it was written in the same signed-in state. */
    chat() {
      const signedIn = Boolean(store()?.signedIn());
      const saved = store()?.greetingChat();
      if (!saved || saved.signedIn !== signedIn || !Array.isArray(saved.messages)) return { signedIn, messages: [] };
      return saved;
    }

    save(messages) {
      store()?.setGreetingChat({ signedIn: Boolean(store()?.signedIn()), messages });
    }

    /** Drops the request on the way (its answer belongs to the old conversation). */
    forget() {
      this.token += 1;
      this.pending = false;
      this.failure = null;
    }

    startOver() {
      this.forget();
      store()?.setGreetingChat(null);
      this.input.value = '';
      this.showEmpty(false);
      this.render();
      if (hoverDevice.matches) this.input.focus();
      else this.querySelector('[data-greeting-title]')?.focus({ preventScroll: true });
    }

    /* --- Sending ---------------------------------------------------------------- */

    /** The bag's items as the server reads them: one per product, up to ten. */
    items() {
      const seen = new Set();
      return this.lines()
        .map((line) => ({ title: line.dataset.title || '', vendor: line.dataset.vendor || '', type: line.dataset.type || '' }))
        .filter((item) => {
          const key = `${item.vendor}\n${item.title}`;
          if (!item.title || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, ITEMS_MAX);
    }

    lines() {
      return [...(this.cart?.querySelectorAll('[data-cart-line]') || [])];
    }

    showEmpty(show) {
      this.empty.hidden = !show;
      this.input.closest('.field')?.classList.toggle('is-invalid', show);
    }

    sendText(raw) {
      if (this.pending) return;
      const text = normalize(raw).slice(0, TEXT_MAX);
      if (!text) {
        this.showEmpty(true);
        this.input.focus();
        return;
      }
      this.showEmpty(false);
      let { messages } = this.chat();
      // A message that got no answer is replaced: the server never gets two of the shopper's in a row.
      if (messages.at(-1)?.role === 'customer') messages = messages.slice(0, -1);
      messages = [...messages, { role: 'customer', text }];
      this.save(messages);
      this.input.value = '';
      this.request(messages);
    }

    retry() {
      const { messages } = this.chat();
      if (!this.pending && messages.at(-1)?.role === 'customer') this.request(messages);
    }

    async request(messages) {
      const token = ++this.token;
      const keepFocus = hoverDevice.matches && this.composer.contains(document.activeElement);
      this.pending = true;
      this.failure = null;
      this.render();
      let data;
      try {
        data = await ai().greeting(messages, this.items());
        if (!normalize(data?.reply) && !normalize(data?.greeting)) throw Object.assign(new Error('empty'), { code: 'model_error' });
      } catch (error) {
        if (token !== this.token) return;
        this.pending = false;
        this.failure = error?.code || 'network';
        this.render();
        return;
      }
      if (token !== this.token) return;
      // Kept exactly as it came back, so AMOR's offer to name an item is not made twice.
      const { _debug, ...answer } = data;
      this.pending = false;
      this.save([...messages, { role: 'amor', ...answer }]);
      this.render(true);
      this.status.textContent = [answer.reply, answer.greeting].filter(Boolean).join(' ');
      if (keepFocus) this.input.focus({ preventScroll: true });
    }

    /* --- Drawing ---------------------------------------------------------------- */

    question() {
      const name = store()?.signedIn() ? ai()?.customer()?.first_name : '';
      return name ? this.strings.questionSignedIn.replace('__NAME__', name) : this.strings.questionGuest;
    }

    /** Small pictures of what is in the bag, one per product, in the order of the bag. */
    renderItems() {
      const seen = new Set();
      const thumbs = this.lines()
        .filter((line) => {
          const key = `${line.dataset.vendor}\n${line.dataset.title}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((line) => {
          const item = element('li', 'greeting__item');
          const image = line.querySelector('.cart-line__media img');
          if (image) {
            const copy = image.cloneNode();
            copy.removeAttribute('loading');
            copy.setAttribute('sizes', '56px');
            copy.alt = line.dataset.title || '';
            item.append(copy);
          } else {
            item.append(element('span', 'greeting__item-empty'));
          }
          return item;
        });
      this.itemList.replaceChildren(...thumbs);
      this.itemList.hidden = !thumbs.length;
    }

    bubble(who, text) {
      const node = element('p', `greeting__bubble greeting__bubble--${who}`, text);
      if (who === 'customer') node.dir = 'auto';
      return node;
    }

    /** "Here we write the note together. For anything else, AMOR BY YOU", with the link. */
    unrelated(message) {
      const node = this.bubble('amor');
      const [before, after = ''] = String(this.strings.unrelated || '').split('__LINK__');
      const url = message.link?.url;
      const link = element('a', 'link', message.link?.label || this.strings.amorByYou);
      link.href = typeof url === 'string' && /^\/(?!\/)/.test(url) ? url : FALLBACK_LINK;
      link.dir = 'ltr';
      node.append(before, link, after);
      return node;
    }

    /** AMOR's sentence, and under it the note as a card with its count and "choose this note". */
    answer(message, index) {
      const nodes = [];
      if (message.status === 'unrelated') nodes.push(this.unrelated(message));
      else if (normalize(message.reply)) nodes.push(this.bubble('amor', message.reply));
      if (message.greeting) {
        const card = element('div', 'greeting__card');
        card.setAttribute('role', 'group');
        card.setAttribute('aria-label', this.strings.card);
        const note = element('p', 'greeting__note', message.greeting);
        note.dir = 'auto';
        const foot = element('div', 'greeting__card-foot');
        const count = element('span', 'greeting__count', this.strings.count.replace('__COUNT__', message.greeting.length));
        count.dir = 'ltr';
        const button = element('button', 'btn btn--sm greeting__choose', this.strings.choose);
        button.type = 'button';
        button.dataset.greetingChoose = index;
        foot.append(count, button);
        card.append(note, foot);
        nodes.push(card);
      }
      return nodes;
    }

    loading() {
      const node = element('p', 'greeting__bubble greeting__bubble--amor greeting__typing');
      const dots = element('span', 'greeting__dots');
      dots.setAttribute('aria-hidden', 'true');
      dots.append(element('span'), element('span'), element('span'));
      node.append(dots, element('span', 'visually-hidden', this.strings.loading));
      return node;
    }

    error(code) {
      const template = this.querySelector('template[data-greeting-error]');
      const box = template.content.firstElementChild.cloneNode(true);
      box.querySelector('[data-greeting-error-text]').textContent =
        code === 'rate_limited' ? this.strings.rateLimited : this.strings.error;
      box.querySelector('[data-greeting-retry]').hidden = LIMITS.includes(code);
      box.querySelector('[data-greeting-worlds]').hidden = !LIMITS.includes(code);
      return box;
    }

    renderPills(texts) {
      this.pills.replaceChildren(
        ...texts.map((text) => {
          const pill = element('button', 'pill', text);
          pill.type = 'button';
          pill.dataset.greetingPill = '';
          pill.disabled = this.pending;
          return pill;
        })
      );
      this.pills.scrollLeft = 0;
    }

    /** Draws the whole screen from the kept conversation; `fresh` after a new answer. */
    render(fresh) {
      const { messages } = this.chat();
      this.renderItems();
      const nodes = [this.bubble('amor', this.question())];
      let lastAnswer = null;
      messages.forEach((message, index) => {
        if (message.role === 'customer') {
          nodes.push(this.bubble('customer', message.text));
          return;
        }
        const parts = this.answer(message, index);
        lastAnswer = parts[0] || lastAnswer;
        nodes.push(...parts);
      });
      if (this.pending) nodes.push(this.loading());
      else if (messages.at(-1)?.role === 'customer') nodes.push(this.error(this.failure || 'network'));
      this.log.replaceChildren(...nodes);

      const hasNote = messages.some((message) => message.role === 'amor' && message.greeting);
      const pills = hasNote ? this.strings.refine : this.strings.examples;
      this.renderPills(Array.isArray(pills) ? pills : []);
      this.input.placeholder = hasNote ? this.input.dataset.placeholderRefine : this.input.dataset.placeholder;
      this.input.disabled = this.pending;
      this.sendButton.disabled = this.pending;
      this.form.setAttribute('aria-busy', String(this.pending));
      this.newButton.hidden = !messages.length;
      this.scrollToEnd(fresh ? lastAnswer : null);
    }

    /** The newest message in view; a new answer taller than the screen shows from its top. */
    scrollToEnd(answer) {
      window.requestAnimationFrame(() => {
        if (this.drawer) {
          this.scroller.scrollTop = this.scroller.scrollHeight;
          if (answer && answer.getBoundingClientRect().top < this.scroller.getBoundingClientRect().top) {
            this.scroller.scrollTop += answer.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top;
          }
        } else if (answer || this.pending) {
          this.composer.scrollIntoView({ block: 'nearest' });
        }
      });
    }
  }

  customElements.define('amor-greeting', AmorGreeting);
})();
